import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, FileSignature, Trash2, Plus, Mail, Download, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const KIND_COLORS = {
  added: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  removed: 'bg-red-100 text-red-700 border-red-200',
  changed: 'bg-amber-100 text-amber-700 border-amber-200',
};

/**
 * Variation of Contract dialog.
 *
 * Opens after a customer record is saved when equipment differs from the
 * "agreed" snapshot (which is captured the first time the customer gets a
 * quote / invoice / RAMS). Admin assigns £ deduct / £ add to each detected
 * change, then chooses Save / Email / Both. Backend generates the PDF, attaches
 * to the customer documents, and optionally emails it for signing.
 */
export const VariationOfContractDialog = ({ open, onOpenChange, customer, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState('');
  const [originalContractSum, setOriginalContractSum] = useState(0);
  const [vatRate, setVatRate] = useState(0);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!open || !customer?.id) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/customers/${customer.id}/variation-diff`).then(res => {
      if (cancelled) return;
      const data = res.data || {};
      setLines((data.lines || []).map(ln => ({ ...ln, deduct: '', add: '' })));
      setOriginalContractSum(data.original_contract_sum || customer.system_total_cost || 0);
    }).catch(() => {
      if (!cancelled) toast.error('Unable to load equipment diff');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [open, customer?.id, customer?.system_total_cost]);

  const updateLine = (idx, key, value) => {
    setLines(prev => prev.map((ln, i) => i === idx ? { ...ln, [key]: value } : ln));
  };
  const removeLine = (idx) => setLines(prev => prev.filter((_, i) => i !== idx));
  const addLine = () => setLines(prev => [...prev, { kind: 'changed', label: 'Manual', description: '', deduct: '', add: '' }]);

  const totalDeduct = lines.reduce((sum, l) => sum + (parseFloat(l.deduct) || 0), 0);
  const totalAdd = lines.reduce((sum, l) => sum + (parseFloat(l.add) || 0), 0);
  const valueOfInstruction = totalAdd - totalDeduct;
  const vatAmount = valueOfInstruction * (parseFloat(vatRate) || 0);
  const adjustedTotal = parseFloat(originalContractSum || 0) + valueOfInstruction + vatAmount;

  const submit = async (action) => {
    if (lines.length === 0 && !reasons.trim()) {
      toast.error('Add at least one change line or enter a reason for the variation');
      return;
    }
    // Variations now roll into the FINAL PAYMENT (no separate invoice). Confirm
    // the impact on the customer's final bill before saving.
    const grossChange = valueOfInstruction + vatAmount;
    if (grossChange !== 0) {
      const currentFinal = parseFloat(customer.final_bill_amount) || 0;
      const newFinal = Math.max(0, currentFinal + grossChange);
      const dir = grossChange > 0 ? 'increase' : 'reduce';
      const ok = window.confirm(
        `This variation will ${dir} the final payment by £${Math.abs(grossChange).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` +
        ((parseFloat(vatRate) || 0) > 0 ? ' (incl. VAT)' : '') +
        `.\n\nFinal payment: £${currentFinal.toLocaleString('en-GB', { minimumFractionDigits: 2 })} → £${newFinal.toLocaleString('en-GB', { minimumFractionDigits: 2 })}\n\nNo separate invoice is raised — this is added to the final payment. Continue?`
      );
      if (!ok) return;
    }
    setSubmitting(true);
    try {
      const payload = {
        reasons,
        original_contract_sum: parseFloat(originalContractSum) || 0,
        vat_rate: parseFloat(vatRate) || 0,
        action,
        lines: lines.map(l => ({
          description: l.description,
          deduct: parseFloat(l.deduct) || 0,
          add: parseFloat(l.add) || 0,
        })),
      };
      const res = await api.post(`/customers/${customer.id}/variation-of-contract`, payload);
      const data = res.data || {};
      if (action === 'email' || action === 'both') {
        if (data.email_sent) {
          toast.success('Variation saved and emailed to customer');
        } else {
          toast.warning(`Saved, but email failed: ${data.email_error || 'unknown'}`);
        }
      } else {
        toast.success('Variation of Contract saved to customer file');
      }
      const fp = data.final_payment;
      if (fp?.applied) {
        toast.success(
          `Final payment updated: £${(fp.old_final_bill_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })} → £${(fp.new_final_bill_amount || 0).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`,
          { duration: 8000 }
        );
        if (fp.final_invoice_flagged) {
          toast.warning(fp.flag_reason || 'Final invoice already paid — raise a supplementary invoice in Xero.', { duration: 12000 });
        } else if (fp.final_invoice_updated) {
          toast.info(`Existing final invoice ${fp.final_invoice_number || ''} updated to the new total.`, { duration: 8000 });
        }
      } else if (fp?.error) {
        toast.warning(`Could not update final payment: ${fp.error}`);
      }
      if (typeof onSaved === 'function') onSaved(data);
      onOpenChange(false);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Failed to create variation: ${typeof detail === 'string' ? detail.slice(0, 200) : 'Unknown'}`);
    } finally {
      setSubmitting(false);
    }
  };

  const fmt = (v) => `£${(parseFloat(v) || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl" data-testid="variation-of-contract-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-amber-600" />
            Raise Variation of Contract
          </DialogTitle>
          <DialogDescription>
            We&apos;ve detected equipment changes since the customer was last emailed. Review the changes, enter
            the deduct / add value for each line, and generate the Variation of Contract document for them to sign.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            <span className="text-sm">Loading equipment changes…</span>
          </div>
        ) : (
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            {lines.length === 0 && (
              <div className="flex items-start gap-2 p-3 rounded border border-amber-200 bg-amber-50 text-sm text-amber-800">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>No equipment changes detected against the agreed snapshot. You can still raise a variation — add a manual change line and/or enter a reason below. The price can stay the same (no £ change required).</span>
              </div>
            )}

            {/* Variation lines */}
            <div className="space-y-2">
              {lines.map((ln, idx) => (
                <div key={idx} className="rounded border border-slate-200 bg-white p-3 space-y-2" data-testid={`variation-line-${idx}`}>
                  <div className="flex items-start gap-2">
                    <Badge variant="outline" className={`${KIND_COLORS[ln.kind] || ''} text-[10px] capitalize`}>
                      {ln.kind}
                    </Badge>
                    <Textarea
                      rows={2}
                      value={ln.description}
                      onChange={e => updateLine(idx, 'description', e.target.value)}
                      placeholder="Description of works (auto-filled from detected change)"
                      className="flex-1 text-sm"
                      data-testid={`variation-line-${idx}-description`}
                    />
                    <Button
                      variant="ghost" size="sm"
                      className="text-red-500 hover:text-red-700 hover:bg-red-50 px-2"
                      onClick={() => removeLine(idx)}
                      data-testid={`variation-line-${idx}-remove`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 ml-[58px]">
                    <div>
                      <label className="text-[11px] text-slate-500 uppercase tracking-wide">£ Deduct</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={ln.deduct}
                        onChange={e => updateLine(idx, 'deduct', e.target.value)}
                        placeholder="0.00"
                        className="h-8 text-sm"
                        data-testid={`variation-line-${idx}-deduct`}
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-slate-500 uppercase tracking-wide">£ Add</label>
                      <Input
                        type="number" step="0.01" min="0"
                        value={ln.add}
                        onChange={e => updateLine(idx, 'add', e.target.value)}
                        placeholder="0.00"
                        className="h-8 text-sm"
                        data-testid={`variation-line-${idx}-add`}
                      />
                    </div>
                  </div>
                </div>
              ))}

              <Button variant="outline" size="sm" onClick={addLine} className="w-full border-dashed" data-testid="variation-add-line">
                <Plus className="w-4 h-4 mr-1" /> Add another change manually
              </Button>
            </div>

            {/* Reasons */}
            <div>
              <label className="text-xs font-semibold text-slate-700 mb-1 block">Reasons for variation to the agreed contract terms</label>
              <Textarea
                rows={3}
                value={reasons}
                onChange={e => setReasons(e.target.value)}
                placeholder="e.g. Customer upgraded battery capacity; delivery delay on original inverter; …"
                data-testid="variation-reasons"
              />
            </div>

            {/* Financial summary */}
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wide">Original Contract Sum</label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={originalContractSum}
                    onChange={e => setOriginalContractSum(e.target.value)}
                    className="h-8"
                    data-testid="variation-original-sum"
                  />
                </div>
                <div>
                  <label className="text-[11px] text-slate-500 uppercase tracking-wide">VAT Rate (%)</label>
                  <Input
                    type="number" step="1" min="0" max="100"
                    value={(parseFloat(vatRate) || 0) * 100}
                    onChange={e => setVatRate((parseFloat(e.target.value) || 0) / 100)}
                    className="h-8"
                    data-testid="variation-vat-rate"
                  />
                  <p className="text-[10px] text-slate-400 mt-0.5">0% UK domestic solar · 20% commercial</p>
                </div>
              </div>
              <div className="border-t border-slate-200 pt-2 grid grid-cols-2 gap-y-1 text-sm">
                <span className="text-slate-500">Total deductions</span>
                <span className="text-right font-medium text-red-600">-{fmt(totalDeduct)}</span>
                <span className="text-slate-500">Total additions</span>
                <span className="text-right font-medium text-emerald-700">+{fmt(totalAdd)}</span>
                <span className="text-slate-500">Value of this instruction</span>
                <span className={`text-right font-semibold ${valueOfInstruction >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt(valueOfInstruction)}</span>
                <span className="text-slate-500">VAT</span>
                <span className="text-right font-medium">{fmt(vatAmount)}</span>
                <span className="text-slate-700 font-semibold">Adjusted total</span>
                <span className="text-right font-bold text-slate-900" data-testid="variation-adjusted-total">{fmt(adjustedTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="flex flex-col sm:flex-row gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="variation-cancel">
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={() => submit('save')}
            disabled={submitting || loading}
            data-testid="variation-save"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Save to file
          </Button>
          <Button
            variant="outline"
            className="border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={() => submit('email')}
            disabled={submitting || loading}
            data-testid="variation-email"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail className="w-4 h-4 mr-2" />}
            Email to customer
          </Button>
          <Button
            className="bg-amber-600 hover:bg-amber-700 text-white"
            onClick={() => submit('both')}
            disabled={submitting || loading}
            data-testid="variation-both"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileSignature className="w-4 h-4 mr-2" />}
            Save &amp; Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default VariationOfContractDialog;
